import { useEventTimezoneQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

/**
 * Formats a date as ISO 8601 with timezone
 * Converts a date with a specific local time to a timezoned date with same local time
 * and returns the ISO 8601 string with timezone
 * @param date - The date to format
 * @param timezone - The timezone to use
 * @returns The formatted date
 */
export const formatDateAsISO8601TZ = (date: Date | null, timezone?: string) => {
  if (!date) {
    return null;
  }
  // Convert to UTC using the specified timezone
  const newDate = zonedTimeToUtc(date, timezone ?? DEFAULT_TIMEZONE);

  return timezone ? `${newDate.toISOString()}[${timezone}]` : newDate.toISOString();
};

export const split86012String = (isoString: string) => {
  const [datetimeRaw, zonePart] = isoString.split('[');

  if (!zonePart) {
    return { datetime: new Date(datetimeRaw), timezone: undefined };
  }

  const timezone = zonePart.replace(']', '');
  const datetime = utcToZonedTime(new Date(datetimeRaw), timezone);

  return { datetime, timezone };
};

export const useGetEventTimezone = (): string | undefined => {
  const { data } = useEventTimezoneQuery({
    variables: { eventId: useEventInfo().eventInfo?.eventId ?? '' },
    batchMode: 'fast',
  });

  return data?.eventById?.info.timezone ?? undefined;
};
